/**
 * draft.ts — a document, as a post draft holds it.
 *
 * A draft is the one place a document is written *before* it is a record, so it
 * is the one place the document's two halves have to travel together: the file
 * (which the PDS already holds, because a document uploads the moment it is
 * picked) and the work done against its pages.
 *
 * Three decisions live here, and the first is the reason this file exists.
 *
 *  - **A document draft names the blob by CID, not by a local file.** An image
 *    or a video draft is device-bound by design — an unuploaded file has nowhere
 *    else to live (see `draftEmbedLocalRef`). A document is the opposite: by the
 *    time there is anything to save, the bytes are on the PDS and permanent
 *    (`verifyBlobAndMakePermanent`), so a draft that pointed at the local file
 *    would throw away the only thing the protocol already has. Naming the CID is
 *    what lets a PDF draft open on another device, and it is why the embed
 *    declares `blobCid` rather than a lexicon `blob`: a draft travels as JSON
 *    through private storage rather than as a repository record, and the shape
 *    that survives that trip is the CID plus its type and size.
 *  - **The page activity is `unknown` to the lexicon and normalized on the way
 *    back in.** The questions are the studio's own `Slide`s — the same objects a
 *    capsule or a FlashBook holds — and the published material record has no
 *    lexicon either (`#/lib/document/material`). Teaching the draft schema that
 *    vocabulary would be a second description of it that could drift from the
 *    first; instead the payload rides along and `normalizeDocumentMaterial`
 *    decides what it is, exactly as it does for a record read off the wire.
 *  - **Nothing here is trusted on the way in.** A draft is written by a client
 *    the server does not know the version of, so every field is checked (or
 *    dropped) and a document with no usable CID is no document at all — which is
 *    what keeps a half-written draft from replacing a real attachment with a
 *    broken card.
 */

import {DRAFT_EMBED_HEADROOM_BYTES, DRAFT_MAX_JSON_BYTES} from './limits'
import {
  type DocumentMaterial,
  normalizeDocumentMaterial,
  trimMaterialToBudget,
} from './material'
import {type DocumentPublishData} from './publish'
import {
  type DocumentAudience,
  type DocumentBlobRef,
  normalizeDocumentAudience,
} from './types'

/** The draft def this module writes, named once. */
export const DRAFT_DOCUMENT_EMBED_TYPE =
  'app.bsky.draft.defs#draftEmbedDocument'

/**
 * A document inside a draft post.
 *
 * Deliberately a *structural* type rather than a generated one: the installed
 * `@atproto/api` build has no draft document in it yet (same reason
 * `#/lib/document/types` carries the embed record's shape), so the shape lives
 * here and is what the converters are written against.
 */
export type DraftDocumentEmbed = {
  $type: typeof DRAFT_DOCUMENT_EMBED_TYPE
  blobCid: string
  mimeType: string
  blobSize?: number
  title?: string
  pageCount?: number
  fileSize?: number
  alt?: string
  /** Curriculum grade code, eg. `Level_B1`. */
  level?: string
  /** Curriculum subject code, eg. `Maths`. */
  subject?: string
  /** The authored page activity, as `DocumentMaterial` (see the header). */
  material?: unknown
}

/**
 * How much of a draft's body is left for page activity, in JSON characters.
 *
 * Measured against the draft *without* its documents, because the activity is
 * the only part of a document draft the transport can refuse: everything else a
 * document contributes is a handful of fields (see `#/lib/document/limits`).
 * A draft whose text alone has already filled the body reports a negative
 * budget, and {@link trimMaterialForDraft} answers that by keeping nothing —
 * which is the right way round, because the author's words are not what should
 * be dropped.
 */
export function draftMaterialBudget(draftWithoutMaterial: string): number {
  return (
    DRAFT_MAX_JSON_BYTES -
    draftWithoutMaterial.length -
    DRAFT_EMBED_HEADROOM_BYTES
  )
}

/**
 * The page activity a draft can keep, and what it could not.
 *
 * Trimmed from the last page backwards, exactly as it is when it is *published*
 * (`trimMaterialToBudget`), so the pages an author wrote first are the ones that
 * survive — and the dropped pages are returned rather than counted, because the
 * only honest way to bound work by the transport is to name what was cut.
 */
export function trimMaterialForDraft(
  material: DocumentMaterial | undefined,
  {remainingBytes}: {remainingBytes: number},
): {material: DocumentMaterial | undefined; droppedPages: number[]} {
  if (!material || material.pages.length === 0) {
    return {material: undefined, droppedPages: []}
  }
  if (remainingBytes <= 0) {
    return {
      material: undefined,
      droppedPages: material.pages.map(row => row.page),
    }
  }
  const {material: trimmed, droppedPages} = trimMaterialToBudget(material, {
    budgetBytes: remainingBytes,
    sizeOf: pages => JSON.stringify({pages}).length,
  })
  return {
    material: trimmed.pages.length > 0 ? trimmed : undefined,
    droppedPages,
  }
}

/** Everything a restored document draft turns back into. */
export type RestoredDraftDocument = {
  blob: DocumentBlobRef
  mimeType: string
  title: string
  pageCount: number
  fileSize: number
  alt: string
  audience?: DocumentAudience
  material?: DocumentMaterial
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function text(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, maxLength)
}

function nonNegativeInteger(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  const whole = Math.floor(value)
  return whole >= 0 ? whole : undefined
}

export function isDraftDocumentEmbed(
  value: unknown,
): value is DraftDocumentEmbed {
  return (
    isObject(value) &&
    value.$type === DRAFT_DOCUMENT_EMBED_TYPE &&
    typeof value.blobCid === 'string'
  )
}

/**
 * The document embed a finished attachment writes into a draft.
 *
 * Takes an upload that has already finished — there is no CID to name until it
 * has, and the composer will not offer to save one that is still going (see the
 * `status === 'done'` guard in `drafts/state/api`).
 */
export function draftDocumentEmbedFrom({
  document,
  alt,
  audience,
  material,
}: {
  document: DocumentPublishData
  alt?: string
  audience?: DocumentAudience
  material?: DocumentMaterial
}): DraftDocumentEmbed {
  const normalized = normalizeDocumentAudience(
    audience?.level,
    audience?.subject,
  )
  const trimmedAlt = text(alt, 10000)
  return {
    $type: DRAFT_DOCUMENT_EMBED_TYPE,
    blobCid: document.blob.ref.$link,
    mimeType: document.mimeType,
    blobSize: document.blob.size,
    title: text(document.title, 3000),
    pageCount: nonNegativeInteger(document.pageCount),
    fileSize: nonNegativeInteger(document.fileSize),
    alt: trimmedAlt,
    level: normalized?.level,
    subject: normalized?.subject,
    // Absent rather than empty: a draft with no activity carries no key, which
    // is what keeps a plain PDF draft the same size it was before this existed.
    material: material && material.pages.length > 0 ? material : undefined,
  }
}

/**
 * The document an embed names, or `undefined` when it names nothing usable.
 *
 * `did` is the author's own, and only matters for one thing: a page's clip whose
 * record does not name an address derives it from its blob (`buildPdsBlobUrl`).
 * A caller without a DID still gets the material — just without those addresses.
 */
export function documentFromDraftEmbed(
  embed: unknown,
  {did}: {did?: string} = {},
): RestoredDraftDocument | undefined {
  if (!isObject(embed)) return undefined
  const blobCid = text(embed.blobCid, 256)
  const mimeType = text(embed.mimeType, 256)
  if (!blobCid || !mimeType) return undefined

  const fileSize = nonNegativeInteger(embed.fileSize) ?? 0
  const blobSize = nonNegativeInteger(embed.blobSize) ?? fileSize
  const pageCount = nonNegativeInteger(embed.pageCount) ?? 0
  const material = normalizeDocumentMaterial(embed.material, {pageCount, did})

  return {
    blob: {
      $type: 'blob',
      ref: {$link: blobCid},
      mimeType,
      size: blobSize,
    },
    mimeType,
    title: text(embed.title, 3000) ?? '',
    pageCount,
    fileSize,
    alt: text(embed.alt, 10000) ?? '',
    audience: normalizeDocumentAudience(embed.level, embed.subject),
    material,
  }
}
